import { useDecision } from '@optimizely/react-sdk'
import { clsx } from 'clsx'
import { useClipboard } from 'use-clipboard-copy'

import { FeaturesList } from '@tribeplatform/react-components/common'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useSlate } from '@tribeplatform/react-slate-kit/hooks'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { EditorSlateExtraContext } from '../types.js'
import { getBlockToCopy, getBlockToPaste, getClipboardText } from './utils.js'

export type BlockActionsProps = {
  id: string
  removable: boolean
  editable: boolean
  acceptsChildren: boolean
  hide?: boolean
  className?: string
  size?: 'sm' | 'md' | 'lg'
  asDropdown?: boolean
  onRename?: () => void
}

export const BlockActions = ({
  id,
  removable,
  editable,
  acceptsChildren,
  hide = false,
  className,
  size = 'sm',
  asDropdown = false,
  onRename,
}: BlockActionsProps) => {
  const { $t } = useI18n()
  const {
    removeBlock,
    addBlock,
    compiledSlate: { blocks },
    extraContext: {
      slate: { activateBlock, openAddBlock },
    },
  } = useSlate<EditorSlateExtraContext>()
  const [decisionBlockCopyPaste] = useDecision(FeaturesList.BlockCopyPaste)

  const { copy } = useClipboard()

  const onRemove = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation()
    const confirmed = await confirm({
      title: $t({
        defaultMessage: 'Are you sure you want to delete this block?',
        id: 'Admin.Editor.Block.Delete.Confirmation',
      }),
      proceedLabel: $t({
        defaultMessage: 'Delete',
        id: 'Generics.Delete',
      }),
      cancelLabel: $t({
        defaultMessage: 'Cancel',
        id: 'Generics.Cancel',
      }),
      danger: true,
    })
    if (!confirmed) {
      return
    }
    removeBlock(id)
  }

  const onActivate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    activateBlock(id)
  }

  const onAddBlock = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    openAddBlock(id)
  }

  const onCopy = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    const blockToCopy = getBlockToCopy(id, blocks)
    if (!blockToCopy) {
      return
    }

    copy(JSON.stringify(blockToCopy))
  }

  const onPaste = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation()
    // The error is handled inside getClipboardText
    const clipboardText = await getClipboardText()
    if (clipboardText === null) {
      toast({
        title: 'Unable to paste block',
        status: 'error',
        description:
          'Please make sure your browser supports clipboard and you have allowed proper access.',
      })
      return
    }

    const newBlock = getBlockToPaste(clipboardText, blocks)
    if (newBlock === null) {
      toast({
        title: 'Unable to paste block',
        status: 'error',
        description: 'Please copy a block before using the paste block action.',
      })
      return
    }

    addBlock(id, 0, {
      blocks: newBlock.blocks,
      rootBlock: newBlock.rootId,
    })
  }

  return (
    <div
      className={clsx(
        'flex flex-row items-center transition-all',
        size === 'sm' && 'gap-0.5',
        size === 'md' && 'gap-1',
        size === 'lg' && 'gap-2',
        hide || (!removable && !editable && !acceptsChildren)
          ? 'opacity-0'
          : 'opacity-100',
        className,
      )}
    >
      {acceptsChildren && (
        <IconButton
          size="sm"
          variant="tertiaryNeutral"
          rounded
          onClick={onAddBlock}
          icon={<SvgIcon name="plus" />}
        />
      )}
      {!acceptsChildren && asDropdown && editable && (
        <IconButton
          size="sm"
          variant="tertiaryNeutral"
          rounded
          onClick={onActivate}
          icon={<SvgIcon name="settings" />}
        />
      )}
      {asDropdown ? (
        <Dropdown placement="bottom-end">
          <Dropdown.ButtonMinimal as="div">
            <IconButton
              size="sm"
              variant="tertiaryNeutral"
              rounded
              icon={<SvgIcon name="dots-horizontal" />}
            />
          </Dropdown.ButtonMinimal>
          <Dropdown.Items>
            {acceptsChildren && (
              <Dropdown.Item
                onClick={onAddBlock}
                leadingIcon={<SvgIcon name="plus" />}
              >
                <T defaultMessage="Add block" id="BlockActions.AddBlock" />
              </Dropdown.Item>
            )}
            {editable && (
              <Dropdown.Item
                onClick={onActivate}
                leadingIcon={<SvgIcon name="settings" />}
              >
                <T defaultMessage="Settings" id="BlockActions.Settings" />
              </Dropdown.Item>
            )}
            {editable && typeof onRename === 'function' && (
              <Dropdown.Item
                onClick={onRename}
                leadingIcon={<SvgIcon name="edit" />}
              >
                <T defaultMessage="Rename" id="BlockActions.Rename" />
              </Dropdown.Item>
            )}
            {decisionBlockCopyPaste.enabled && (
              <Dropdown.Item
                onClick={onCopy}
                leadingIcon={<SvgIcon name="copy" />}
              >
                <T defaultMessage="Copy block" id="BlockActions.CopyBlock" />
              </Dropdown.Item>
            )}
            {decisionBlockCopyPaste.enabled && acceptsChildren && (
              <Dropdown.Item
                onClick={onPaste}
                leadingIcon={<SvgIcon name="clipboard" />}
              >
                <T defaultMessage="Paste block" id="BlockActions.PasteBlock" />
              </Dropdown.Item>
            )}
            {removable && (
              <Dropdown.Item
                onClick={onRemove}
                leadingIcon={<SvgIcon name="trash" />}
              >
                <T defaultMessage="Remove" id="Generics.Remove" />
              </Dropdown.Item>
            )}
          </Dropdown.Items>
        </Dropdown>
      ) : (
        <>
          {removable && (
            <IconButton
              icon={<SvgIcon name="trash" />}
              rounded
              variant="tertiaryNeutral"
              size="md"
              onClick={onRemove}
            />
          )}
          {editable && (
            <IconButton
              icon={<SvgIcon name="settings" />}
              rounded
              variant="tertiaryNeutral"
              size="md"
              onClick={onActivate}
            />
          )}
          {acceptsChildren && (
            <IconButton
              icon={<SvgIcon name="plus" />}
              rounded
              variant="tertiaryNeutral"
              size="md"
              onClick={onAddBlock}
            />
          )}
        </>
      )}
    </div>
  )
}
